import {
    Tag,
    Star,
    Heart,
    Bookmark,
    Flag,
    Target,
    Zap,
    Award,
    Shield,
    Clock,
    User,
    Users,
    Building,
    Briefcase,
    Code,
    Database,
    Settings,
    Wrench,
    Lightbulb,
    Rocket,
    Trophy,
    Crown,
    Diamond
} from 'lucide-react';
import {LucideIcon} from 'lucide-react';
import {Tag as TagModel, TagScope} from '../types';

// Predefined colors for tags (expanded palette)
export const PREDEFINED_COLORS = [
    // Reds & Pinks
    '#FF6B6B',
    '#E74C3C',
    '#FF69B4',
    '#DC143C',
    // Oranges & Yellows
    '#FF8C00',
    '#F7B801',
    '#FFD700',
    '#FFA500',
    // Greens
    '#2ECC71',
    '#1ABC9C',
    '#32CD32',
    '#228B22',
    // Blues & Cyans
    '#3498DB',
    '#45B7D1',
    '#4ECDC4',
    '#00CED1',
    // Purples & Violets
    '#9B59B6',
    '#5F4B8B',
    '#8A2BE2',
    '#6A5ACD',
    // Browns & Neutrals
    '#8B4513',
    '#A0522D',
    '#708090',
    '#2F4F4F'
];

// Available icons for tags
export interface TagIcon {
    name: string;
    component: LucideIcon;
}

export const PREDEFINED_ICONS: TagIcon[] = [
    {name: 'Tag', component: Tag},
    {name: 'Star', component: Star},
    {name: 'Heart', component: Heart},
    {name: 'Bookmark', component: Bookmark},
    {name: 'Flag', component: Flag},
    {name: 'Target', component: Target},
    {name: 'Zap', component: Zap},
    {name: 'Award', component: Award},
    {name: 'Shield', component: Shield},
    {name: 'Clock', component: Clock},
    {name: 'User', component: User},
    {name: 'Users', component: Users},
    {name: 'Building', component: Building},
    {name: 'Briefcase', component: Briefcase},
    {name: 'Code', component: Code},
    {name: 'Database', component: Database},
    {name: 'Settings', component: Settings},
    {name: 'Wrench', component: Wrench},
    {name: 'Lightbulb', component: Lightbulb},
    {name: 'Rocket', component: Rocket},
    {name: 'Trophy', component: Trophy},
    {name: 'Crown', component: Crown},
    {name: 'Diamond', component: Diamond}
];

// Default values for new tags
export const TAG_DEFAULTS = {
    colour: PREDEFINED_COLORS[0],
    icon: 'Tag',
    isActive: true
};

// Utility functions for tag name processing
export const normalizeTagInput = (input: string): string => {
    return input
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
};

export const finalizeTagName = (input: string): string => {
    return normalizeTagInput(input).replace(/-+/g, '-').replace(/^-|-$/g, '');
};

// Get icon component by name
export const getTagIconComponent = (iconName?: string): LucideIcon => {
    const icon = PREDEFINED_ICONS.find(i => i.name === iconName);
    return icon?.component || Tag; // Default to Tag if not found
};

// Validate tag name
export const isValidTagName = (name: string): boolean => {
    const trimmed = name.trim();
    return trimmed.length > 0 && trimmed.length <= 50;
};

// Get a random color from the palette
export const getRandomTagColor = (): string => {
    return PREDEFINED_COLORS[
        Math.floor(Math.random() * PREDEFINED_COLORS.length)
    ];
};

// Get a random icon name
export const getRandomTagIcon = (): string => {
    return PREDEFINED_ICONS[Math.floor(Math.random() * PREDEFINED_ICONS.length)]
        .name;
};

// ============================ Hierarchy & scope ============================

// Returns true if a tag may be applied in the given scope. Tags with no explicit
// appliesTo (legacy rows) are treated as Any so they remain selectable everywhere.
export const tagAppliesTo = (tag: TagModel, scope: TagScope): boolean => {
    const applies = tag.appliesTo ?? TagScope.Any;
    return (applies & scope) !== 0;
};

export interface TagTreeNode {
    tag: TagModel;
    children: TagTreeNode[];
}

// Build a forest of TagTreeNodes from a flat tag list. Tags whose parent is not in
// the provided set are treated as roots (so a filtered subset still renders).
export const buildTagTree = (tags: TagModel[]): TagTreeNode[] => {
    const byId = new Map<string, TagModel>();
    tags.forEach(t => {
        if (t.id) byId.set(t.id, t);
    });

    const nodes = new Map<string, TagTreeNode>();
    tags.forEach(t => {
        if (t.id) nodes.set(t.id, {tag: t, children: []});
    });

    const roots: TagTreeNode[] = [];
    tags.forEach(t => {
        if (!t.id) return;
        const node = nodes.get(t.id)!;
        const parentId = t.parentTagId;
        if (parentId && nodes.has(parentId) && byId.has(parentId)) {
            nodes.get(parentId)!.children.push(node);
        } else {
            roots.push(node);
        }
    });

    const sortNodes = (list: TagTreeNode[]) => {
        list.sort((a, b) => a.tag.name.localeCompare(b.tag.name));
        list.forEach(n => sortNodes(n.children));
    };
    sortNodes(roots);

    return roots;
};

// Walk up to the top-level ancestor of a tag (cycle-guarded). Returns the tag's
// own id if it has no parent.
export const getRootId = (
    tagId: string,
    tagsById: Record<string, TagModel>
): string => {
    let currentId = tagId;
    const seen = new Set<string>();
    while (!seen.has(currentId)) {
        seen.add(currentId);
        const parentId = tagsById[currentId]?.parentTagId;
        if (!parentId || !tagsById[parentId]) return currentId;
        currentId = parentId;
    }
    return currentId;
};

// All descendant ids of a tag (excluding itself). Cycle-guarded.
export const getDescendantIds = (
    tagId: string,
    tags: TagModel[]
): Set<string> => {
    const childrenByParent = new Map<string, string[]>();
    tags.forEach(t => {
        if (t.id && t.parentTagId) {
            const list = childrenByParent.get(t.parentTagId) ?? [];
            list.push(t.id);
            childrenByParent.set(t.parentTagId, list);
        }
    });

    const result = new Set<string>();
    const stack = [...(childrenByParent.get(tagId) ?? [])];
    while (stack.length) {
        const id = stack.pop()!;
        if (result.has(id)) continue;
        result.add(id);
        stack.push(...(childrenByParent.get(id) ?? []));
    }
    return result;
};

export interface FlatTreeRow {
    tag: TagModel;
    depth: number;
}

// Flatten a tag forest into indented display rows (depth-first, name-sorted).
export const flattenTreeForDisplay = (
    tags: TagModel[]
): FlatTreeRow[] => {
    const rows: FlatTreeRow[] = [];
    const walk = (nodes: TagTreeNode[], depth: number) => {
        nodes.forEach(n => {
            rows.push({tag: n.tag, depth});
            walk(n.children, depth + 1);
        });
    };
    walk(buildTagTree(tags), 0);
    return rows;
};
